import { useEffect, useReducer, type JSX } from "react";

import { useSelector } from "react-redux";

import { Button, Tabs } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    StyledErrorInfoIcon,
    StyledIconWrapper,
} from "app/pages/sales-intelligence/common-components/export-table/styles";
import { ExportTablePreloader } from "app/pages/sales-intelligence/pages/management/components/common/Preloaders/ExportTablePreloader";
import {
    selectCurrentExportTab,
    selectPreviousExportTab,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/common";
import { getIntegrationNameFromUrl } from "app/pages/sales-intelligence/common-components/export-table/getIntegrationNameFromUrl";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { useIsCompanyAsLeadClaimEnabled } from "../../hooks/useIsCompanyAsLeadClaimEnabled";

import {
    StyledTabList,
    StyledTabsWrapper,
    StyledTab,
    StyledTabButtonWrapper,
    StyledTabPanel,
    StyledTabHeaderLabel,
} from "../../style";
import { EXPORT_ADD_BUTTON_TITLE } from "../../translations/translations";
import { Accounts } from "../Accounts/Accounts";
import { CompanyAsLeads } from "../CompanyAsLeads/CompanyAsLeads";
import { Contacts } from "../Contacts/Contacts";
import { Leads } from "../Leads/Leads";

import type { ExportTabsProps } from "./type";

export const ExportTabHeader = ({
    onAddFields,
    onSelect,
    selectedIndex,
    tabsWithErrors,
    isLeadsVisible,
    isLoading,
    tabsDisplayName,
}: ExportTabsProps) => {
    const { integrationName } = getIntegrationNameFromUrl() as { integrationName: Integration };
    const isCompanyAsLeadExportClaimEnabled = useIsCompanyAsLeadClaimEnabled(integrationName);

    const wrappedWithLoadingState = (component: JSX.Element) => (isLoading ? <ExportTablePreloader /> : component);

    const previousTab = useSelector(selectPreviousExportTab);
    const currentTab = useSelector(selectCurrentExportTab);

    const initialState = { accounts: false, contacts: false, leads: false, companyAsLeads: false };

    function reducer(state: typeof initialState, { type, payload }: { type: ExportName; payload: boolean }) {
        return { ...state, [type]: payload };
    }

    const [visitedInvalidTabs, dispatchVisitedInvalidTab] = useReducer(reducer, initialState);

    useEffect(() => {
        if (previousTab && tabsWithErrors.includes(previousTab)) {
            dispatchVisitedInvalidTab({ type: previousTab, payload: true });
        }

        if (!tabsWithErrors.includes(currentTab)) {
            dispatchVisitedInvalidTab({ type: currentTab, payload: false });
        }
    }, [previousTab, tabsWithErrors, currentTab]);

    const renderTabHeaders = () => {
        const tabsQuantity = tabsDisplayName.length;

        return tabsDisplayName.map((name, index) => {
            const isVisible = tabsQuantity - 1 === index && !isLeadsVisible ? false : true;
            let validInternalName = name === "companies" ? "accounts" : name;
            if (name === "leads" && isCompanyAsLeadExportClaimEnabled) {
                validInternalName = "companyAsLeads";
            }

            const isSelected = tabsDisplayName[selectedIndex] === name;
            const isVisitedTab = visitedInvalidTabs[validInternalName as ExportName];

            const isErrorIconVisible = !isSelected && isVisitedTab;

            return isVisible ? (
                <StyledTab key={name}>
                    {isErrorIconVisible ? (
                        <StyledIconWrapper>
                            <StyledErrorInfoIcon
                                iconName="info-full"
                                size="xs"
                            />
                        </StyledIconWrapper>
                    ) : null}
                    <StyledTabHeaderLabel>{name}</StyledTabHeaderLabel>
                </StyledTab>
            ) : null;
        });
    };

    return (
        <Tabs
            selectedIndex={selectedIndex}
            onSelect={onSelect}
            forceRenderTabPanel
        >
            <StyledTabList>
                <StyledTabsWrapper>{renderTabHeaders()}</StyledTabsWrapper>
                <StyledTabButtonWrapper>
                    <Button
                        type="outlined"
                        onClick={onAddFields}
                    >
                        {translate(EXPORT_ADD_BUTTON_TITLE)}
                    </Button>
                </StyledTabButtonWrapper>
            </StyledTabList>
            <StyledTabPanel>{wrappedWithLoadingState(<Accounts />)}</StyledTabPanel>
            <StyledTabPanel>{wrappedWithLoadingState(<Contacts />)}</StyledTabPanel>
            <StyledTabPanel>
                {wrappedWithLoadingState(isCompanyAsLeadExportClaimEnabled ? <CompanyAsLeads /> : <Leads />)}
            </StyledTabPanel>
        </Tabs>
    );
};
